import { Fragment } from 'react/jsx-runtime';
import { ValueSlot } from '../ValueSlot/ValueSlot';
import { ConditionNode } from './types';

/**
 * Parses text content and creates ValueSlot components with support for nested blocks
 */
export class ContentParser {
  private key = 0;
  private colorDefinitions: Map<string, string> = new Map();

  setColorDefinitions(colorDefinitions: Map<string, string>) {
    this.colorDefinitions = colorDefinitions;
  }

  /**
   * Parses condition text with nested diamond and circle blocks
   * Example: "<<do i hit [border]?> or <(y-position) > 100 >>"
   */
  parseCondition(conditionText: string): React.ReactNode {
    if (!conditionText) return null;

    this.key = 0;
    const tree = this.parseConditionTree(conditionText);

    return this.renderConditionTree(tree);
  }

  /**
   * Parses block content with inline values (e.g., "move (12)")
   */
  parseBlockContent(contentText: string): React.ReactNode {
    if (!contentText) return null;

    const parts: React.ReactNode[] = [];
    let remaining = contentText;
    this.key = 0;

    while (remaining) {
      const result = this.tryParseParenthesizedValue(remaining) || this.tryParseContentText(remaining);

      if (result) {
        if (result.element) parts.push(result.element);
        remaining = result.remaining;
      } else {
        break;
      }
    }

    return parts.length > 0 ? <>{parts}</> : contentText;
  }

  /**
   * Parses a condition string into a tree structure
   */
  private parseConditionTree(text: string): ConditionNode[] {
    const nodes: ConditionNode[] = [];
    let i = 0;

    while (i < text.length) {
      // Skip whitespace
      while (i < text.length && /\s/.test(text[i])) {
        i++;
      }
      if (i >= text.length) break;

      // Try to parse diamond block <...>
      if (text[i] === '<' && !this.isEscaped(text, i)) {
        const result = this.parseDiamondBlock(text, i);
        if (result) {
          nodes.push(result.node);
          i = result.endIndex;
          continue;
        }
      }

      // Try to parse circle block (...)
      if (text[i] === '(') {
        const result = this.parseCircleBlock(text, i);
        if (result) {
          nodes.push(result.node);
          i = result.endIndex;
          continue;
        }
      }

      // Try to parse operator block [...]
      if (text[i] === '[') {
        const result = this.parseOperatorBlock(text, i);
        if (result) {
          nodes.push(result.node);
          i = result.endIndex;
          continue;
        }
      }

      // Try to parse variable $...
      if (text[i] === '$') {
        const result = this.parseVariable(text, i);
        if (result) {
          nodes.push(result.node);
          i = result.endIndex;
          continue;
        }
      }

      // Parse plain text
      const result = this.parsePlainText(text, i);
      if (result) {
        nodes.push(result.node);
        i = result.endIndex;
      } else {
        i++;
      }
    }

    return nodes;
  }

  /**
   * Parses a diamond block with optional color: <content> or <#color:content>
   */
  private parseDiamondBlock(text: string, startIndex: number): { node: ConditionNode; endIndex: number } | null {
    let depth = 0;
    let i = startIndex;

    // Find matching closing bracket
    while (i < text.length) {
      if (text[i] === '<' && !this.isEscaped(text, i)) depth++;
      if (text[i] === '>' && !this.isEscaped(text, i)) {
        depth--;
        if (depth === 0) {
          const content = text.substring(startIndex + 1, i);

          const { color, innerContent } = this.extractColor(content);

          return {
            node: {
              type: 'diamond',
              value: '',
              color: color,
              children: this.parseConditionTree(innerContent),
            },
            endIndex: i + 1,
          };
        }
      }
      i++;
    }

    return null;
  }

  /**
   * Parses a circle block with optional color: (content) or (#color:content)
   */
  private parseCircleBlock(text: string, startIndex: number): { node: ConditionNode; endIndex: number } | null {
    let depth = 0;
    let i = startIndex;

    // Find matching closing bracket
    while (i < text.length) {
      if (text[i] === '(') depth++;
      if (text[i] === ')') {
        depth--;
        if (depth === 0) {
          const content = text.substring(startIndex + 1, i);

          const { color, innerContent } = this.extractColor(content);

          return {
            node: {
              type: 'circle',
              value: '',
              color: color,
              children: this.parseConditionTree(innerContent),
            },
            endIndex: i + 1,
          };
        }
      }
      i++;
    }

    return null;
  }

  /**
   * Parses an operator block [...]
   */
  private parseOperatorBlock(text: string, startIndex: number): { node: ConditionNode; endIndex: number } | null {
    const endIndex = text.indexOf(']', startIndex);
    if (endIndex === -1) return null;

    const content = text.substring(startIndex + 1, endIndex);
    return {
      node: {
        type: 'operator',
        value: content,
      },
      endIndex: endIndex + 1,
    };
  }

  /**
   * Parses a variable $name
   */
  private parseVariable(text: string, startIndex: number): { node: ConditionNode; endIndex: number } | null {
    const match = text.substring(startIndex).match(/^\$(\w+)/);
    if (!match) return null;

    return {
      node: {
        type: 'circle',
        value: match[0],
      },
      endIndex: startIndex + match[0].length,
    };
  }

  /**
   * Parses plain text until a special character
   */
  private parsePlainText(text: string, startIndex: number): { node: ConditionNode; endIndex: number } | null {
    const match = text.substring(startIndex).match(/^(\\[<>()[\]$\\]|[^<>()[\]$\s\\]+|\s+)/);
    if (!match) return null;

    // Remove escape backslashes from the matched text
    const value = match[0].replace(/\\([<>()[\]$\\])/g, '$1');

    return {
      node: {
        type: 'text',
        value: value,
      },
      endIndex: startIndex + match[0].length,
    };
  }

  /**
   * Extracts color from content in format #colorKey:content
   */
  private extractColor(content: string): { color: string | undefined; innerContent: string } {
    const colorMatch = content.match(/^#([a-zA-Z0-9]+):(.*)$/);

    if (colorMatch) {
      const colorKey = colorMatch[1];
      const resolvedColor = this.colorDefinitions.get(colorKey) || colorKey;
      return {
        color: resolvedColor,
        innerContent: colorMatch[2],
      };
    }
    return {
      color: undefined,
      innerContent: content,
    };
  }

  /**
   * Renders a condition tree as React elements
   */
  private renderConditionTree(nodes: ConditionNode[]): React.ReactNode {
    return (
      <>
        {nodes.map(node => (
          <span key={this.key++} style={{ margin: '0 0.25rem' }}>
            {this.renderConditionNode(node)}
          </span>
        ))}
      </>
    );
  }

  /**
   * Renders a single condition node
   */
  private renderConditionNode(node: ConditionNode): React.ReactNode {
    switch (node.type) {
      case 'diamond':
        return (
          <ValueSlot type="diamond" color={node.color}>
            {node.children && this.renderConditionTree(node.children)}
          </ValueSlot>
        );

      case 'circle':
        return (
          <ValueSlot type="round" color={node.color}>
            {node.value || (node.children && this.renderConditionTree(node.children))}
          </ValueSlot>
        );

      case 'operator':
        return <ValueSlot type="hexagon">{node.value}</ValueSlot>;

      case 'text':
        return node.value;

      default:
        return null;
    }
  }

  private tryParseParenthesizedValue(text: string) {
    const match = text.match(/^(\\\(.*?\\\)|\([^)]+\))/);
    if (!match) return null;

    const value = match[1].replace(/\\\(/g, '(').replace(/\\\)/g, ')');
    const innerValue = value.slice(1, -1);

    return {
      element: (
        <ValueSlot key={this.key++} type="round">
          {innerValue}
        </ValueSlot>
      ),
      remaining: text.slice(match[0].length),
    };
  }

  private tryParseContentText(text: string) {
    const match = text.match(/^([^(\\]+|\\(?!\()|\\$)/);
    if (!match) return null;

    return {
      element: match[1],
      remaining: text.slice(match[0].length),
    };
  }

  private isEscaped(text: string, index: number): boolean {
    return index > 0 && text[index - 1] === '\\';
  }
}
